// src/pages/customer-panel/personalize/PersonalizeEmailsPage.js
import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Button, IconButton, Chip, CircularProgress, Alert, Snackbar, Tooltip, Checkbox,
    TextField, InputAdornment, Select, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions,
    LinearProgress, FormControl,
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import PublicIcon from '@mui/icons-material/Public';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import SyncIcon from '@mui/icons-material/Sync';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const GREEN_ACCENT = '#10b981';

// ── Processing Dialog ──
function ProcessingDialog({ open, items, action, onClose, title }) {
    const [current, setCurrent] = useState(0);
    const [done, setDone] = useState(false);

    useEffect(() => {
        if (!open || !items.length) return;
        setCurrent(0); setDone(false);
        let cancelled = false;
        (async () => {
            for (let i = 0; i < items.length; i++) {
                if (cancelled) break;
                try { await action(items[i]); } catch { /* continue */ }
                setCurrent(i + 1);
            }
            setDone(true);
        })();
        return () => { cancelled = true; };
    }, [open, items, action]);

    if (!open) return null;
    return (
        <Dialog open maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
            <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>{title}</DialogTitle>
            <DialogContent>
                <Typography sx={{ fontFamily, mb: 2, color: '#64748b' }}>Processing {current} of {items.length}...</Typography>
                <LinearProgress variant="determinate" value={items.length ? Math.round((current / items.length) * 100) : 0}
                    sx={{ height: 8, borderRadius: 4, bgcolor: `${GREEN_ACCENT}20`, '& .MuiLinearProgress-bar': { bgcolor: GREEN_ACCENT } }} />
                {done && <Alert severity="success" sx={{ mt: 2, fontFamily, borderRadius: '10px' }}>All items processed!</Alert>}
            </DialogContent>
            {done && <DialogActions sx={{ px: 3, pb: 3 }}><Button onClick={onClose} variant="contained" sx={{ fontFamily, fontWeight: 600, bgcolor: GREEN_ACCENT, borderRadius: '10px', textTransform: 'none', '&:hover': { bgcolor: '#059669' } }}>Done</Button></DialogActions>}
        </Dialog>
    );
}

// ── Fetch Emails Dialog ──
function FetchEmailsDialog({ open, onClose, onFetch }) {
    const [months, setMonths] = useState(1);
    const [loading, setLoading] = useState(false);

    const handleFetch = async () => {
        setLoading(true);
        await onFetch(months);
        setLoading(false);
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
            <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>Fetch Emails</DialogTitle>
            <DialogContent>
                <Typography sx={{ fontFamily, color: '#64748b', mb: 2 }}>How many months of emails would you like to fetch?</Typography>
                <FormControl fullWidth size="small">
                    <Select value={months} onChange={e => setMonths(e.target.value)} sx={{ fontFamily }}>
                        {[1, 2, 3, 6, 12].map(m => <MenuItem key={m} value={m} sx={{ fontFamily }}>{m} month{m !== 1 ? 's' : ''}</MenuItem>)}
                    </Select>
                </FormControl>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 3 }}>
                <Button onClick={onClose} disabled={loading} sx={{ fontFamily, fontWeight: 600, color: '#64748b', textTransform: 'none' }}>Cancel</Button>
                <Button onClick={handleFetch} disabled={loading} variant="contained"
                    startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SyncIcon />}
                    sx={{ fontFamily, fontWeight: 600, bgcolor: GREEN_ACCENT, borderRadius: '10px', textTransform: 'none', '&:hover': { bgcolor: '#059669' } }}>
                    {loading ? 'Fetching...' : 'Fetch Emails'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

// ── Main Page ──
export default function PersonalizeEmailsPage() {
    const [emailStatus, setEmailStatus] = useState(null);
    const [statusLoading, setStatusLoading] = useState(true);

    const [threads, setThreads] = useState([]);
    const [totalThreads, setTotalThreads] = useState(0);
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [threadsLoading, setThreadsLoading] = useState(false);

    const [selectedIds, setSelectedIds] = useState(new Set());
    const [showFetchDialog, setShowFetchDialog] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [processingState, setProcessingState] = useState({ open: false, items: [], action: null, title: '' });

    const showMsg = (message, severity = 'success') => setSnackbar({ open: true, message, severity });

    // Load email status
    useEffect(() => {
        document.title = 'Personalize Emails | Customer Panel';
        (async () => {
            setStatusLoading(true);
            try {
                const { data } = await axiosInstance.get('/employee-email/status');
                setEmailStatus(data);
            } catch { setEmailStatus(null); }
            setStatusLoading(false);
        })();
    }, []);

    // Load threads
    const loadThreads = useCallback(async () => {
        if (!emailStatus?.is_connected) return;
        setThreadsLoading(true);
        try {
            const { data } = await axiosInstance.get('/employee-email/threads', { params: { page, limit, search: searchTerm, status: statusFilter } });
            setThreads(data.threads || []); setTotalThreads(data.total || 0);
        } catch { setThreads([]); setTotalThreads(0); }
        setThreadsLoading(false);
    }, [page, limit, searchTerm, statusFilter, emailStatus]);

    useEffect(() => { loadThreads(); }, [loadThreads]);

    const handleConnect = async (emailType) => {
        try {
            const { data } = await axiosInstance.get(`/employee-email/${emailType}/auth/url`);
            if (data.url) window.location.href = data.url;
        } catch (err) { showMsg(err.response?.data?.message || 'Failed to get auth URL', 'error'); }
    };

    const handleDisconnect = async () => {
        if (!window.confirm('Are you sure you want to disconnect your email?')) return;
        try {
            await axiosInstance.delete('/employee-email/disconnect');
            const { data } = await axiosInstance.get('/employee-email/status');
            setEmailStatus(data); showMsg('Email disconnected');
        } catch (err) { showMsg(err.response?.data?.message || 'Failed to disconnect', 'error'); }
    };

    const handleFetchEmails = async (months) => {
        try {
            await axiosInstance.post('/employee-email/fetch', { months });
            showMsg('Emails fetched successfully!'); setShowFetchDialog(false); loadThreads();
        } catch (err) { showMsg(err.response?.data?.message || 'Failed to fetch emails', 'error'); }
    };

    // Bulk actions
    const handleBulkTrain = (action) => {
        const sel = threads.filter(t => selectedIds.has(t.id));
        if (!sel.length) { showMsg('Select at least one thread.', 'error'); return; }
        setProcessingState({
            open: true, items: sel, title: action === 'train' ? 'Training Email Threads' : 'Untraining Email Threads',
            action: async (thread) => { await axiosInstance.post('/employee-email/threads/training-status', { thread_ids: [thread.id], is_trained: action === 'train' }); },
        });
    };

    const handleBulkDelete = () => {
        const sel = threads.filter(t => selectedIds.has(t.id));
        if (!sel.length) { showMsg('Select at least one thread.', 'error'); return; }
        if (!window.confirm(`Delete ${sel.length} thread(s)? This cannot be undone.`)) return;
        setProcessingState({
            open: true, items: sel, title: 'Deleting Email Threads',
            action: async (thread) => { await axiosInstance.post('/employee-email/threads/bulk-delete', { thread_ids: [thread.id] }); },
        });
    };

    const handleBulkPublic = async (isPublic) => {
        const sel = threads.filter(t => selectedIds.has(t.id));
        if (!sel.length) { showMsg('Select at least one thread.', 'error'); return; }
        if (isPublic) {
            const untrained = sel.filter(t => !t.isTrained);
            if (untrained.length) { showMsg(`${untrained.length} thread(s) must be trained first.`, 'error'); return; }
        }
        if (!window.confirm(`Mark ${sel.length} thread(s) as ${isPublic ? 'public' : 'private'}?`)) return;
        try {
            await axiosInstance.post('/employee-email/threads/is-public', { thread_ids: sel.map(t => t.id), is_public: isPublic });
            showMsg(`Threads marked as ${isPublic ? 'public' : 'private'}!`); setSelectedIds(new Set()); loadThreads();
        } catch { showMsg('Failed to update', 'error'); }
    };

    const closeProcessing = () => { setProcessingState({ open: false, items: [], action: null, title: '' }); setSelectedIds(new Set()); loadThreads(); };
    const toggleSelect = id => setSelectedIds(prev => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s; });
    const toggleAll = () => setSelectedIds(prev => prev.size === threads.length ? new Set() : new Set(threads.map(t => t.id)));

    const totalPages = Math.ceil(totalThreads / limit);
    const formatDate = d => { if (!d) return '--'; try { return new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }); } catch { return '--'; } };

    if (statusLoading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}><CircularProgress sx={{ color: GREEN_ACCENT }} /></Box>;
    }

    return (
        <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1100, mx: 'auto' }}>
            <Box sx={{ mb: 3 }}>
                <Typography variant="h4" sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: { xs: '1.5rem', md: '2rem' } }}>Personalize Emails</Typography>
                <Typography variant="body2" sx={{ fontFamily, color: '#64748b', mt: 0.5 }}>Connect and manage your email conversations</Typography>
            </Box>

            {!emailStatus?.is_connected ? (
                /* Connect Email Card */
                <Paper sx={{ p: 4, borderRadius: '16px', border: '1px solid', borderColor: 'grey.100' }}>
                    <Typography variant="h6" sx={{ fontFamily, fontWeight: 700, mb: 1 }}>Connect Your Personal Email</Typography>
                    <Typography sx={{ fontFamily, color: '#64748b', mb: 3 }}>Connect your Gmail or Outlook account to import and manage your email conversations.</Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                        <Button onClick={() => handleConnect('gmail')} variant="outlined"
                            sx={{ fontFamily, fontWeight: 600, py: 3, borderRadius: '12px', textTransform: 'none', borderColor: 'grey.300', color: '#ea4335', '&:hover': { borderColor: '#ea4335', bgcolor: '#ea433508' } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <EmailIcon sx={{ color: '#ea4335' }} />
                                Connect Gmail
                            </Box>
                        </Button>
                        <Button onClick={() => handleConnect('outlook')} variant="outlined"
                            sx={{ fontFamily, fontWeight: 600, py: 3, borderRadius: '12px', textTransform: 'none', borderColor: 'grey.300', color: '#0078d4', '&:hover': { borderColor: '#0078d4', bgcolor: '#0078d408' } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <EmailIcon sx={{ color: '#0078d4' }} />
                                Connect Outlook
                            </Box>
                        </Button>
                    </Box>
                </Paper>
            ) : (
                <>
                    {/* Email Status Card */}
                    <Paper sx={{ p: 3, mb: 3, borderRadius: '16px', border: '1px solid', borderColor: 'grey.100' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <EmailIcon sx={{ fontSize: 32, color: emailStatus.email_type === 'gmail' ? '#ea4335' : '#0078d4' }} />
                                <Box>
                                    <Typography sx={{ fontFamily, fontWeight: 600, color: '#1e293b' }}>{emailStatus.details?.name || emailStatus.details?.email || 'Connected'}</Typography>
                                    {emailStatus.details?.email && <Typography sx={{ fontFamily, fontSize: '0.8rem', color: '#64748b' }}>{emailStatus.details.email}</Typography>}
                                </Box>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <Button size="small" startIcon={<SyncIcon />} onClick={() => setShowFetchDialog(true)} variant="contained"
                                    sx={{ fontFamily, fontWeight: 600, textTransform: 'none', bgcolor: GREEN_ACCENT, borderRadius: '10px', '&:hover': { bgcolor: '#059669' } }}>Fetch Emails</Button>
                                <Button size="small" variant="outlined" startIcon={<LinkOffIcon />} onClick={handleDisconnect}
                                    sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderColor: 'grey.300', color: '#64748b', borderRadius: '10px' }}>Disconnect</Button>
                            </Box>
                        </Box>
                    </Paper>

                    {/* Threads Table */}
                    <Paper sx={{ borderRadius: '16px', border: '1px solid', borderColor: 'grey.100', overflow: 'hidden' }}>
                        <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'grey.100' }}>
                            <Typography sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: '1.1rem' }}>Email Conversations</Typography>
                            {selectedIds.size > 0 && (
                                <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
                                    <Typography sx={{ fontFamily, fontWeight: 600, color: GREEN_ACCENT, fontSize: '0.85rem' }}>{selectedIds.size} selected</Typography>
                                    <Button size="small" startIcon={<CheckCircleIcon />} onClick={() => handleBulkTrain('train')} sx={{ fontFamily, fontWeight: 600, textTransform: 'none', bgcolor: '#10b981', color: '#fff', borderRadius: '8px', '&:hover': { bgcolor: '#059669' } }}>Trained</Button>
                                    <Button size="small" startIcon={<CancelIcon />} onClick={() => handleBulkTrain('untrain')} sx={{ fontFamily, fontWeight: 600, textTransform: 'none', bgcolor: '#f59e0b', color: '#fff', borderRadius: '8px', '&:hover': { bgcolor: '#d97706' } }}>Untrained</Button>
                                    <Button size="small" startIcon={<PublicIcon />} onClick={() => handleBulkPublic(true)} sx={{ fontFamily, fontWeight: 600, textTransform: 'none', bgcolor: '#3b82f6', color: '#fff', borderRadius: '8px', '&:hover': { bgcolor: '#2563eb' } }}>Public</Button>
                                    <Button size="small" startIcon={<VisibilityOffIcon />} onClick={() => handleBulkPublic(false)} sx={{ fontFamily, fontWeight: 600, textTransform: 'none', bgcolor: '#64748b', color: '#fff', borderRadius: '8px', '&:hover': { bgcolor: '#475569' } }}>Private</Button>
                                    <Button size="small" startIcon={<DeleteIcon />} onClick={handleBulkDelete} sx={{ fontFamily, fontWeight: 600, textTransform: 'none', bgcolor: '#ef4444', color: '#fff', borderRadius: '8px', '&:hover': { bgcolor: '#dc2626' } }}>Delete</Button>
                                    <Button size="small" onClick={() => setSelectedIds(new Set())} sx={{ fontFamily, textTransform: 'none', color: GREEN_ACCENT }}>Clear</Button>
                                </Box>
                            )}
                        </Box>

                        {/* Search + Filter */}
                        <Box sx={{ p: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', borderBottom: '1px solid', borderColor: 'grey.100' }}>
                            <TextField size="small" placeholder="Search email threads..." value={searchTerm} onChange={e => { setSearchTerm(e.target.value); setPage(1); }}
                                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#94a3b8' }} /></InputAdornment> }}
                                sx={{ flex: 1, minWidth: 200, fontFamily }} />
                            <FormControl size="small" sx={{ minWidth: 140 }}>
                                <Select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }} sx={{ fontFamily }}>
                                    <MenuItem value="all" sx={{ fontFamily }}>All</MenuItem>
                                    <MenuItem value="trained" sx={{ fontFamily }}>Trained</MenuItem>
                                    <MenuItem value="untrained" sx={{ fontFamily }}>Untrained</MenuItem>
                                </Select>
                            </FormControl>
                            <Tooltip title="Refresh"><IconButton onClick={loadThreads} sx={{ color: '#64748b' }}><RefreshIcon /></IconButton></Tooltip>
                        </Box>

                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                        <TableCell padding="checkbox"><Checkbox size="small" checked={selectedIds.size === threads.length && threads.length > 0} indeterminate={selectedIds.size > 0 && selectedIds.size < threads.length} onChange={toggleAll} sx={{ color: GREEN_ACCENT, '&.Mui-checked': { color: GREEN_ACCENT }, '&.MuiCheckbox-indeterminate': { color: GREEN_ACCENT } }} /></TableCell>
                                        {['Subject', 'Participants', 'Status', 'Visibility', 'Last Message', 'Actions'].map(h => (
                                            <TableCell key={h} sx={{ fontFamily, fontWeight: 700, color: '#64748b', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{h}</TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {threadsLoading ? (
                                        <TableRow><TableCell colSpan={7} sx={{ textAlign: 'center', py: 6 }}><CircularProgress size={32} sx={{ color: GREEN_ACCENT }} /></TableCell></TableRow>
                                    ) : threads.length === 0 ? (
                                        <TableRow><TableCell colSpan={7} sx={{ textAlign: 'center', py: 6 }}>
                                            <EmailIcon sx={{ fontSize: 48, color: '#e2e8f0', mb: 1, display: 'block', mx: 'auto' }} />
                                            <Typography sx={{ fontFamily, color: '#94a3b8', mb: 2 }}>No email conversations</Typography>
                                            <Button startIcon={<SyncIcon />} variant="outlined" onClick={() => setShowFetchDialog(true)}
                                                sx={{ fontFamily, fontWeight: 600, borderColor: GREEN_ACCENT, color: GREEN_ACCENT, borderRadius: '10px', textTransform: 'none', '&:hover': { borderColor: '#059669', bgcolor: `${GREEN_ACCENT}08` } }}>
                                                Fetch Your Emails
                                            </Button>
                                        </TableCell></TableRow>
                                    ) : threads.map(t => (
                                        <TableRow key={t.id} sx={{ '&:hover': { bgcolor: '#f8fafc' } }}>
                                            <TableCell padding="checkbox"><Checkbox size="small" checked={selectedIds.has(t.id)} onChange={() => toggleSelect(t.id)} sx={{ color: GREEN_ACCENT, '&.Mui-checked': { color: GREEN_ACCENT } }} /></TableCell>
                                            <TableCell sx={{ fontFamily, fontWeight: 600, color: '#1e293b', maxWidth: 250 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <EmailIcon sx={{ fontSize: 18, color: '#94a3b8', flexShrink: 0 }} />
                                                    <Box sx={{ minWidth: 0 }}>
                                                        <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '0.85rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={t.subject}>{t.subject || '(No Subject)'}</Typography>
                                                        <Typography sx={{ fontFamily, fontSize: '0.7rem', color: '#94a3b8' }}>{t.messageCount} message{t.messageCount !== 1 ? 's' : ''}</Typography>
                                                    </Box>
                                                </Box>
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.8rem', color: '#64748b', maxWidth: 180 }}>
                                                <Typography sx={{ fontFamily, fontSize: '0.8rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                    {t.participants?.slice(0, 2).join(', ')}{t.participants?.length > 2 ? ` +${t.participants.length - 2}` : ''}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Chip label={t.isTrained ? 'Trained' : 'Untrained'} size="small"
                                                    sx={{ fontFamily, fontWeight: 600, fontSize: '0.75rem', bgcolor: t.isTrained ? '#d1fae5' : '#fef3c7', color: t.isTrained ? '#10b981' : '#f59e0b' }} />
                                            </TableCell>
                                            <TableCell>
                                                {t.isTrained ? (
                                                    t.isPublic ? <Chip icon={<PublicIcon sx={{ fontSize: 14 }} />} label="Public" size="small" sx={{ fontFamily, fontWeight: 600, fontSize: '0.7rem', bgcolor: '#dbeafe', color: '#3b82f6' }} />
                                                    : <Chip icon={<VisibilityOffIcon sx={{ fontSize: 14 }} />} label="Private" size="small" sx={{ fontFamily, fontWeight: 600, fontSize: '0.7rem', bgcolor: '#f1f5f9', color: '#64748b' }} />
                                                ) : <Typography sx={{ fontFamily, color: '#cbd5e1', fontSize: '0.8rem' }}>&mdash;</Typography>}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, color: '#64748b', fontSize: '0.85rem' }}>{formatDate(t.lastMessageDate)}</TableCell>
                                            <TableCell>
                                                <Tooltip title="Edit Summary"><IconButton size="small" sx={{ color: '#3b82f6' }}><EditIcon fontSize="small" /></IconButton></Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        {totalPages > 1 && (
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, borderTop: '1px solid', borderColor: 'grey.100' }}>
                                <Typography sx={{ fontFamily, fontSize: '0.8rem', color: '#64748b' }}>
                                    {(page - 1) * limit + 1}–{Math.min(page * limit, totalThreads)} of {totalThreads}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <IconButton size="small" disabled={page === 1} onClick={() => setPage(p => p - 1)}><NavigateBeforeIcon /></IconButton>
                                    <IconButton size="small" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}><NavigateNextIcon /></IconButton>
                                </Box>
                            </Box>
                        )}
                    </Paper>
                </>
            )}

            {/* Dialogs */}
            <FetchEmailsDialog open={showFetchDialog} onClose={() => setShowFetchDialog(false)} onFetch={handleFetchEmails} />
            <ProcessingDialog open={processingState.open} items={processingState.items} action={processingState.action} onClose={closeProcessing} title={processingState.title} />

            <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar(p => ({ ...p, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
                <Alert onClose={() => setSnackbar(p => ({ ...p, open: false }))} severity={snackbar.severity} sx={{ fontFamily, borderRadius: '10px' }}>{snackbar.message}</Alert>
            </Snackbar>
        </Box>
    );
}
